import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { RequiredMark } from '../../components/ui/RequiredMark';
import { TagChipsInput } from '../../components/ui/TagChipsInput';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  CannedResponse,
  CannedResponseUpsert,
  createCannedResponse,
  deleteCannedResponse,
  listCannedResponses,
  updateCannedResponse,
} from '../../services/customerSuccessEngagementApi';
import { listServiceCatalogItems, ServiceCatalogItem } from '../../services/serviceCatalogApi';

type FormState = Omit<CannedResponseUpsert, 'sortOrder'>;

const emptyForm: FormState = {
  title: '',
  body: '',
  tags: '',
  serviceCatalogItemId: null,
  includeInAiSuggest: true,
  isActive: true,
};

export function CannedResponsesPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('customer-success.engagement');
  const [items, setItems] = useState<CannedResponse[]>([]);
  const [catalog, setCatalog] = useState<ServiceCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const [list, catalogResult] = await Promise.all([
        listCannedResponses(),
        listServiceCatalogItems({ page: 1, pageSize: 500 }),
      ]);
      setItems(list);
      setCatalog(catalogResult.items);
    } catch {
      toast.error('Falha ao carregar respostas prontas.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  function startCreate(): void {
    setEditingId(null);
    setForm(emptyForm);
    expandForm();
  }

  function startEdit(item: CannedResponse): void {
    setEditingId(item.id);
    setForm({
      title: item.title,
      body: item.body,
      tags: item.tags ?? '',
      serviceCatalogItemId: item.serviceCatalogItemId ?? null,
      includeInAiSuggest: item.includeInAiSuggest,
      isActive: item.isActive,
    });
    expandForm();
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !form.title.trim() || !form.body.trim()) return;

    setSaving(true);
    try {
      const payload: CannedResponseUpsert = {
        ...form,
        title: form.title.trim(),
        body: form.body.trim(),
        tags: form.tags?.trim() || null,
        serviceCatalogItemId: form.serviceCatalogItemId || null,
        sortOrder: editingId
          ? items.find((item) => item.id === editingId)?.sortOrder ?? items.length
          : items.length,
      };

      if (editingId) {
        await updateCannedResponse(editingId, payload);
        toast.success('Resposta pronta atualizada.');
        collapseForm(() => {
          setEditingId(null);
          setForm(emptyForm);
        });
      } else {
        await createCannedResponse(payload);
        toast.success('Resposta pronta criada.');
        handleCreateSuccess(() => {
          setEditingId(null);
          setForm(emptyForm);
        });
      }
      setEditingId(null);
      await loadData();
    } catch {
      toast.error('Falha ao salvar resposta pronta.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: CannedResponse): Promise<void> {
    if (!canWrite) return;
    const ok = await confirm({
      title: 'Remover resposta pronta',
      message: `Remover "${item.title}"?`,
    });
    if (!ok) return;
    try {
      await deleteCannedResponse(item.id);
      toast.success('Resposta removida.');
      await loadData();
    } catch {
      toast.error('Falha ao remover.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Respostas prontas"
        description="Biblioteca de textos reutilizáveis no atendimento. Podem alimentar a sugestão de IA."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/customer-success/settings?tab=emails" className="erp-btn-secondary">
              Templates de e-mail
            </Link>
            <Link to="/customer-success/automations" className="erp-btn-secondary">
              Automações
            </Link>
            <Link to="/customer-success/survey-forms" className="erp-btn-secondary">
              Formulários de pesquisa
            </Link>
            {canWrite ? (
              <button type="button" className="erp-btn-primary" onClick={startCreate}>
                Nova resposta
              </button>
            ) : null}
          </div>
        }
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar resposta' : 'Nova resposta'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(() => { setEditingId(null); setForm(emptyForm); })}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form-grid">
            <label className="erp-label erp-form-grid-full">
              Título <RequiredMark />
              <input
                className="erp-input"
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
                required
              />
            </label>
            <label className="erp-label erp-form-grid-full">
              Texto <RequiredMark />
              <textarea
                className="erp-input min-h-[8rem]"
                value={form.body}
                onChange={(event) => setForm({ ...form, body: event.target.value })}
                required
              />
            </label>
            <div className="erp-label">
              Tags
              <TagChipsInput
                value={form.tags}
                onChange={(tags) => setForm({ ...form, tags })}
                placeholder="Ex.: senha, reset, fatura"
              />
              <p className="erp-hint mt-1">Enter ou vírgula para adicionar; clique no × para remover.</p>
            </div>
            <label className="erp-label">
              Item de catálogo
              <select
                className="erp-input"
                value={form.serviceCatalogItemId ?? ''}
                onChange={(event) =>
                  setForm({
                    ...form,
                    serviceCatalogItemId: event.target.value || null,
                  })
                }
              >
                <option value="">Nenhum</option>
                {catalog.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.includeInAiSuggest}
                onChange={(event) => setForm({ ...form, includeInAiSuggest: event.target.checked })}
              />
              Incluir nas sugestões de IA
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
              />
              Ativa
            </label>
            <FormActionBar
              className="erp-btn-actions erp-form-grid-full"
              onCancel={() => toggleOrCancel(() => { setEditingId(null); setForm(emptyForm); })}
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <p className="text-sm text-secondary-500">Nenhuma resposta pronta cadastrada.</p>
      ) : (
        <div className="erp-table-wrap">
          <div className="erp-table-scroll">
            <table className="erp-table w-full">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Catálogo</th>
                  <th className="w-32">Status</th>
                  {canWrite ? <th className="w-24" /> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const tags = (item.tags ?? '')
                    .split(',')
                    .map((part) => part.trim())
                    .filter(Boolean);

                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="font-medium text-secondary-900">{item.title}</div>
                        {tags.length > 0 ? (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {tags.map((tag) => (
                              <span
                                key={`${item.id}-${tag}`}
                                className="inline-flex items-center rounded-full border border-secondary-300 bg-white px-2 py-0.5 text-[11px] text-secondary-700"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </td>
                      <td>{item.serviceCatalogItemName ?? '—'}</td>
                      <td>
                        <ActiveStatusBadge active={item.isActive} feminine />
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex justify-end gap-1">
                            <EditIconButton onClick={() => startEdit(item)} />
                            <DeleteIconButton onClick={() => void handleDelete(item)} />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PageShell>
  );
}
